import { inject } from '@angular/core';
import { HttpInterceptorFn } from '@angular/common/http';
import { Router } from '@angular/router';
import { EMPTY } from 'rxjs';
import { UserService } from '../services/user.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

/**
 * HTTP interceptor that attaches the Bearer token to all outgoing requests.
 * Also validates token before making requests to prevent unnecessary API calls
 * with expired tokens.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const userService = inject(UserService);
  const router = inject(Router);
  const modalService = inject(NgbModal);

  // Skip auth for external third-party URLs (e.g. Nominatim geocoding)
  if (req.url.includes('nominatim.openstreetmap.org')) {
    return next(req);
  }

  // Skip auth for public endpoints and 2FA login verification (which uses temp_token in body)
  const isPublicEndpoint = req.url.includes('/oauth/token') ||
                           req.url.includes('/oauth/login') ||
                           req.url.includes('/oauth/verify-2fa') ||
                           req.url.includes('/forgot-password') ||
                           req.url.includes('/reset-password') ||
                           req.url.includes('/users/confirm/') ||
                           req.url.includes('/auth/login') ||
                           req.url.includes('/auth/2fa/');

  if (isPublicEndpoint) {
    return next(req);
  }

  const token = userService.getToken();

  // No token - let the request proceed (error interceptor will handle 401)
  if (!token) {
    return next(req);
  }

  // Check if token is expired before making the request
  if (userService.isTokenExpired()) {
    userService.clearSessionLocally();
    modalService.dismissAll();
    router.navigate(['/account/login'], {
      queryParams: { reason: 'session_expired' }
    });
    // Cancel the request - no point sending with expired token
    return EMPTY;
  }

  // Attach token to request
  const clonedRequest = req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`
    }
  });

  return next(clonedRequest);
};
